import { Fragment, useState } from 'react';
import {
  Dialog, DialogBackdrop, DialogPanel, DialogTitle, Transition, TransitionChild,
} from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import SuccessTickIcon from './SuccessTickIcon';

export const Modal = ({
  heading, subHeading, isOpen, onCloseHandler, isForgotPassword,
}: {
  heading: string,
  subHeading?: string,
  isOpen: boolean,
  onCloseHandler?: () => void;
  isForgotPassword?: boolean
}) => {
  const [open, setOpen] = useState(isOpen);

  if (isForgotPassword) {
    return (
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setOpen} onClick={onCloseHandler}>
          <DialogBackdrop
            transition
            className={[
              'fixed inset-0 bg-doqit-purple/20 backdrop-blur-[5px] transition-opacity',
              'data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200',
              'data-[enter]:ease-out data-[leave]:ease-in',
            ].join(' ')}
          />
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel
                  className="relative w-full max-w-[388px] rounded-[12px] bg-doqit-purple-25 px-8 py-12 text-center shadow-[0_2px_10px_rgba(57,38,79,0.2)] transition-all"
                >
                  <SuccessTickIcon className="mx-auto h-[100px] w-[100px]" />
                  <DialogTitle
                    as="h3"
                    className="mt-8 text-center font-body font-bold text-[22px] leading-[28px] text-doqit-purple"
                  >
                    {heading}
                  </DialogTitle>
                  {subHeading && (
                    <p className="mt-4 mx-auto max-w-[280px] font-body font-medium text-[17px] leading-[24px] text-doqit-purple">
                      {subHeading}
                    </p>
                  )}
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={setOpen} onClick={onCloseHandler}>
        <DialogBackdrop
          transition
          className={[
            'fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity',
            'data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200',
            'data-[enter]:ease-out data-[leave]:ease-in',
          ].join(' ')}
        />

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex mt-60 justify-center p-4 text-center items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left
                shadow-xl transition-all w-64 h-60"
              >
                <div>
                  <div className="dialog-done-panel-text mx-auto flex h-12 w-12 items-center justify-center rounded-full">
                    <CheckIcon className="h-12 w-12 text-green-600 font-semibold" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900 my-5 text-center">
                      {heading}
                    </DialogTitle>
                    <div className="mt-2 w-52 pt-4">
                      <p className="text-sm text-black-500">
                        {subHeading}
                      </p>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Modal;
